<template>
  <b-modal
    id="modal-bulk-upload"
    ref="modalBulkUpload"
    title="Bulk Unenrollment - Upload Excel"
    size="lg"
    @hidden="resetModal"
    @ok="handleUpload">
    <div>
      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <i class="fe fe-info me-2"></i>
        <strong>Instructions:</strong>
        <ul class="mb-0 mt-2">
          <li>Download the template Excel file first</li>
          <li>Upload the filled Excel file (.xlsx format only)</li>
        </ul>
      </div>

      <!-- File Upload -->
      <div class="form-group">
        <label class="form-label">Upload Excel File <span class="text-danger">*</span></label>
        <div class="custom-file">
          <input 
            type="file" 
            class="custom-file-input" 
            id="excelFile"
            accept=".xlsx"
            @change="handleFileChange"
            ref="fileInput">
          <label class="custom-file-label" for="excelFile">
            {{ selectedFileName || 'Choose Excel file...' }}
          </label>
        </div>
        <small class="form-text text-muted">
          Accepted format: .xlsx only
        </small>
      </div>

      <!-- Global Reason -->
      <!-- <div class="form-group">
        <label class="form-label">Global Reason</label>
        <textarea 
          v-model="globalReason"
          class="form-control"
          rows="3"
          placeholder="Enter a reasons..."></textarea>
      </div> -->

      <!-- Selected File Info -->
      <div v-if="selectedFile" class="alert alert-success">
        <i class="fe fe-file-text me-2"></i>
        <strong>Selected file:</strong> {{ selectedFile.name }}
        <br>
        <small>Size: {{ formatFileSize(selectedFile.size) }}</small>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <button 
        type="button" 
        class="btn btn-secondary" 
        @click="cancel()">
        Cancel
      </button>
      <button 
        type="button" 
        class="btn btn-primary"
        :disabled="!selectedFile || isUploading"
        @click="ok()">
        <span v-if="isUploading">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Mengunggah...
        </span>
        <span v-else>
          <i class="fe fe-upload me-2"></i>
          Upload
        </span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ModalBulkUpload',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      globalReason: '',
      isUploading: false
    };
  },
  computed: {
    courseId() {
      return this.selectedCourse?.training_event_course_id || this.selectedCourse?.id;
    }
  },
  methods: {
    ...mapActions('mitraUnenrollment', ['bulkUnenrollment']),
    
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.name.endsWith('.xlsx')) {
          this.$swal({
            title: 'Tipe File Tidak Valid',
            text: 'Pilih file Excel dengan format .xlsx',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal({
            title: 'File Terlalu Besar',
            text: 'Ukuran file tidak boleh lebih dari 5MB',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          this.resetFile();
          return;
        }

        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },
    
    resetFile() {
      this.selectedFile = null;
      this.selectedFileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    resetModal() {
      this.resetFile();
      this.globalReason = '';
      this.isUploading = false;
    },
    
    async handleUpload(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault();
      
      if (!this.selectedFile) {
        this.$swal({
          title: 'File Belum Dipilih',
          text: 'Pilih file Excel terlebih dahulu',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      if (!this.courseId) {
        this.$swal({
          title: 'Error',
          text: 'ID pelatihan tidak ditemukan',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isUploading = true;

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.globalReason) {
          formData.append('global_reason', this.globalReason);
        }

        // Call API with courseId
        const result = await this.bulkUnenrollment({
          courseId: this.courseId,
          formData
        });

        // Close upload modal
        this.$refs.modalBulkUpload.hide();

        // Build HTML content for swal
        const htmlContent = this.buildResultHtml(result);

        // Show success message with data mapping
        const errors = result?.data?.errors || [];
        this.$swal({
          title: errors.length ? 'Upload Selesai dengan Error' : 'Upload Berhasil',
          html: htmlContent,
          icon: errors.length ? 'warning' : 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: errors.length ? '#f6c343' : '#00c9a7',
          width: errors.length ? '1100px' : '720px'
        });

        // Emit success to refresh data
        this.$emit('upload-success', result);
      } catch (error) {
        const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Gagal upload file';
        
        this.$swal({
          title: 'Unggah Gagal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isUploading = false;
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    buildResultHtml(result) {
      const message = result.message || 'Upload unenrollment selesai diproses.';
      const data = result.data || {};
      const submitted = Array.isArray(data) ? data : (data.submitted || []);
      const errors = data.errors || [];
      
      let html = `<p style="margin-bottom: 16px;">${message}</p>`;
      
      if (submitted.length > 0) {
        let totalEnrollments = 0;
        submitted.forEach(batch => {
          const enrollments = batch.unenrolled_participants?.enrollments || [];
          totalEnrollments += enrollments.length;
        });

        html += `
          <div style="text-align: left; background: #f8f9fa; padding: 16px; border-radius: 8px; max-height: 300px; overflow-y: auto;">
            <p style="margin-bottom: 12px;"><strong>Total batch berhasil:</strong> ${submitted.length}</p>
            <p style="margin-bottom: 12px;"><strong>Total peserta berhasil:</strong> ${totalEnrollments}</p>
            <hr style="margin: 12px 0;">
        `;

        submitted.forEach((batch) => {
          const enrollments = batch.unenrolled_participants?.enrollments || [];
          html += `
            <div style="margin-bottom: 12px; padding: 10px; background: #fff; border-radius: 4px; border: 1px solid #dee2e6;">
              <p style="margin: 0 0 8px 0;"><strong>Batch ID:</strong> ${this.escapeHtml(batch.batch_id)}</p>
              <p style="margin: 0 0 4px 0;"><strong>Peserta (${enrollments.length}):</strong></p>
              <ul style="margin: 0; padding-left: 20px; font-size: 13px;">
          `;
          
          enrollments.forEach(enrollment => {
            const note = enrollment.note ? ` - <em>${this.escapeHtml(enrollment.note)}</em>` : '';
            html += `<li>ID: ${this.escapeHtml(enrollment.partner_tna_enrollment_id)}${note}</li>`;
          });
          
          html += `
              </ul>
            </div>
          `;
        });

        html += `</div>`;
      } else {
        html += `
          <div style="text-align: left; background: #f8f9fa; padding: 16px; border-radius: 8px;">
            <p>Perhatian</p>
            <p style="margin: 0;">
              Tidak ada data unenrollment yang berhasil diproses. Cek detail error di bawah jika tersedia.
            </p>
          </div>
        `;
      }

      if (errors.length > 0) {
        html += `
          <div style="margin-top: 16px; text-align: left; background: #fff; padding: 16px; border-radius: 8px; max-height: 420px; overflow: auto;">
            <p style="margin-bottom: 8px; color: #e63757;"><strong>Detail error (${errors.length} baris)</strong></p>
            <table style="width: 100%; min-width: 980px; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #ffebee;">
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Row</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Sheet</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">NIK</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Enrollment ID</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Batch</th>
                  <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pesan Error</th>
                </tr>
              </thead>
              <tbody>
        `;
        errors.slice(0, 50).forEach((item) => {
          html += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.row || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.sheet || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.nik || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.enrollment_id || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${this.escapeHtml(item.training_event_id || '-')}</td>
              <td style="padding: 8px; border: 1px solid #ddd; color: #e63757;">${this.escapeHtml(item.message || item.reason || 'Data tidak valid')}</td>
            </tr>
          `;
        });
        html += `</tbody></table>`;
        if (errors.length > 50) {
          html += `<p class="mb-0 mt-2 text-muted">Masih ada ${errors.length - 50} error lain. Perbaiki error dari atas dulu lalu upload ulang.</p>`;
        }
        html += `</div>`;
      }

      return html;
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.custom-file-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
